import { useMemo, useState } from 'react';
import type { TableProps } from 'antd';
import {
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  TableOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { ScanText } from 'lucide-react';

import { Bento, DataTable, MenuThaoTac, PageHeader, StatTile } from '../../../components/shared';
import { Badge, Button, Empty, Input, Select } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useRoutePermission } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { ngayVN, soVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { useTenNguoi } from '../../xu-ly/hooks/useTenNguoi';
import { useTenModel } from '../components/ChonModel';
import { HopLoaiChungTu } from '../components/HopLoaiChungTu';
import { BADGE_DANG_DUNG, NHAN_CHE_DO, NHAN_LOAI } from '../constants';
import { useDanhSachLoaiChungTu, useGhiLoaiChungTu, useThongKeLoaiChungTu } from '../hooks/useLoaiChungTu';
import { locLoaiChungTu } from '../nghiep-vu/loai-chung-tu';
import type { LocTrangThai } from '../nghiep-vu/loai-chung-tu';
import { DOCUMENT_TYPES } from '../types';
import type { DocumentType, OcrSchemaListItem } from '../types';

/**
 * Thiết lập Chứng từ OCR (cổng tenant, `/xu-ly/cau-hinh-ocr`, phân hệ `OCR_CONFIG`): danh sách loại chứng
 * từ của module `ocr` (`GET /api/v1/m/ocr/schemas`), tạo mới, bật/tắt, xoá; cấu hình trường và bảng
 * ở màn chi tiết. Tải hết danh sách rồi lọc ở client: mỗi tenant vài chục loại.
 */
export function DanhSachLoaiChungTuView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const di = useDieuHuong();
  const tenNguoi = useTenNguoi();
  const tenModel = useTenModel();
  const q = useDanhSachLoaiChungTu();
  const thongKe = useThongKeLoaiChungTu();
  const ghi = useGhiLoaiChungTu();
  const coTao = useRoutePermission('CREATE');
  const coSua = useRoutePermission('UPDATE');
  const coXoa = useRoutePermission('DELETE');

  const [tuKhoa, setTuKhoa] = useState('');
  const [loai, setLoai] = useState<DocumentType | undefined>();
  const [trangThai, setTrangThai] = useState<LocTrangThai>('tat-ca');
  const [moTao, setMoTao] = useState(false);
  const [canXoa, setCanXoa] = useState<OcrSchemaListItem | null>(null);

  const tatCa = q.data ?? [];
  const dong = useMemo(() => locLoaiChungTu(q.data ?? [], { tuKhoa, loai, trangThai }), [q.data, tuKhoa, loai, trangThai]);
  const coLoc = tuKhoa.trim() !== '' || loai !== undefined || trangThai !== 'tat-ca';
  const dangTai = q.isLoading;
  const tieuDe = t('nav.muc.thietLapChungTuOcr');
  const tk = thongKe.data;

  const batTat = (s: OcrSchemaListItem) =>
    ghi.sua.mutate({ id: s.id, du: { is_active: !s.is_active } }, { onError: (err) => toast.error(dichLoi(err)) });

  const columns: TableProps<OcrSchemaListItem>['columns'] = [
    {
      title: t('cauHinhOcr.cotLoai'),
      width: 300,
      render: (_v, s) => (
        <button
          type="button"
          className="flex min-w-0 max-w-xs flex-col text-left"
          onClick={() => di(`/xu-ly/cau-hinh-ocr/${s.id}`)}
        >
          <span className="truncate text-sm font-medium text-content-primary" title={s.name}>
            {s.name}
          </span>
          <span className="truncate font-mono text-2xs text-content-muted">{s.code}</span>
          {s.description && (
            <span className="truncate text-2xs text-content-muted" title={s.description}>
              {s.description}
            </span>
          )}
        </button>
      ),
    },
    {
      title: t('cauHinhOcr.cotPhanLoai'),
      dataIndex: 'type',
      width: 140,
      render: (v: DocumentType) => <Badge variant="neutral">{t(NHAN_LOAI[v])}</Badge>,
    },
    {
      title: t('cauHinhOcr.cotModel'),
      width: 240,
      render: (_v, s) => (
        <div className="flex min-w-0 flex-col">
          <span className="text-xs text-content-secondary">{t(NHAN_CHE_DO[s.extraction_mode])}</span>
          {s.extraction_mode === 'OCR_THEN_MAP' && (
            <span className="truncate text-2xs text-content-muted">
              {t('cauHinhOcr.buocDoc', { ten: tenModel(s.ocr_reader_provider) })}
            </span>
          )}
          <span className="truncate text-2xs text-content-muted">
            {s.extraction_mode === 'OCR_THEN_MAP'
              ? t('cauHinhOcr.buocDien', { ten: tenModel(s.ocr_provider) })
              : tenModel(s.ocr_provider)}
          </span>
        </div>
      ),
    },
    {
      title: t('cauHinhOcr.cotCauTruc'),
      width: 150,
      render: (_v, s) => (
        <div className="flex flex-col text-xs tabular-nums text-content-secondary">
          <span>{t('cauHinhOcr.soTruong', { so: soVN(s.field_count, locale) })}</span>
          <span>{t('cauHinhOcr.soBang', { so: soVN(s.table_count, locale) })}</span>
        </div>
      ),
    },
    {
      title: t('cauHinhOcr.cotChungTu'),
      dataIndex: 'document_count',
      width: 110,
      align: 'right',
      render: (v: number) => <span className="text-xs tabular-nums text-content-secondary">{soVN(v, locale)}</span>,
    },
    {
      title: t('chung.trangThai'),
      width: 120,
      render: (_v, s) => {
        const b = BADGE_DANG_DUNG[s.is_active ? 'bat' : 'tat'];
        return <Badge variant={b.variant}>{t(b.khoa)}</Badge>;
      },
    },
    {
      title: t('cauHinhOcr.cotTao'),
      width: 160,
      render: (_v, s) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-xs text-content-secondary">{tenNguoi(s.created_by)}</span>
          <span className="text-2xs text-content-muted">{ngayVN(s.created_at)}</span>
        </div>
      ),
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      fixed: 'right',
      render: (_v, s) => (
        <MenuThaoTac
          tenBanGhi={s.name}
          muc={[
            {
              khoa: 'xem',
              nhan: t('cauHinhOcr.xemCauHinh'),
              icon: <EyeOutlined />,
              nhom: 'xem',
              onChon: () => di(`/xu-ly/cau-hinh-ocr/${s.id}`),
            },
            {
              khoa: 'bat-tat',
              nhan: t(s.is_active ? 'cauHinhOcr.tatLoai' : 'cauHinhOcr.batLoai'),
              moTa: t(s.is_active ? 'cauHinhOcr.tatLoaiMo' : 'cauHinhOcr.batLoaiMo'),
              icon: s.is_active ? <PauseCircleOutlined /> : <PlayCircleOutlined />,
              hien: coSua,
              onChon: () => batTat(s),
            },
            {
              khoa: 'xoa',
              nhan: t('cauHinhOcr.xoaLoai'),
              icon: <DeleteOutlined />,
              nhom: 'pha-huy',
              hien: coXoa,
              // Có quyền nhưng đang có chứng từ: hiện mờ kèm lý do, server vẫn là chốt chặn.
              voHieu: s.document_count > 0,
              moTa: s.document_count > 0 ? t('cauHinhOcr.xoaChanMo', { so: soVN(s.document_count, locale) }) : undefined,
              onChon: () => {
                ghi.xoa.reset();
                setCanXoa(s);
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[
            { label: t('nav.nhom.xuLyTaiLieu') },
            { label: t('nav.muc.cauHinhOcr') },
            { label: tieuDe },
          ]}
          titleIcon={<ScanText size={18} className="text-primary-600" />}
          title={tieuDe}
          subtitle={t('cauHinhOcr.moTa')}
          actions={
            <>
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => {
                  void q.refetch();
                  void thongKe.refetch();
                }}
              >
                <ReloadOutlined className={q.isFetching ? 'animate-spin' : ''} />
              </Button>
              {coTao && (
                <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setMoTao(true)}>
                  {t('cauHinhOcr.taoLoai')}
                </Button>
              )}
            </>
          }
        />

        {!thongKe.error && (
          <Bento>
            <StatTile
              nhan={t('cauHinhOcr.tkTong')}
              so={tk ? soVN(tk.total_schemas, locale) : '—'}
              sub={t('cauHinhOcr.tkTongMo')}
              icon={<FileTextOutlined />}
              tone="primary"
            />
            <StatTile
              nhan={t('cauHinhOcr.tkDangDung')}
              so={tk ? soVN(tk.active_schemas, locale) : '—'}
              sub={t('cauHinhOcr.tkDangDungMo')}
              icon={<PlayCircleOutlined />}
              tone="success"
            />
            <StatTile
              nhan={t('cauHinhOcr.tkTruong')}
              so={tk ? soVN(tk.total_fields, locale) : '—'}
              sub={t('cauHinhOcr.tkTruongMo')}
              icon={<UnorderedListOutlined />}
              tone="neutral"
            />
            <StatTile
              nhan={t('cauHinhOcr.tkBang')}
              so={tk ? soVN(tk.total_tables, locale) : '—'}
              sub={t('cauHinhOcr.tkBangMo')}
              icon={<TableOutlined />}
              tone="neutral"
            />
          </Bento>
        )}

        <div className="flex flex-wrap items-center gap-2.5 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
          <Input
            allowClear
            className="w-full sm:w-80"
            prefix={<SearchOutlined className="text-content-muted" />}
            placeholder={t('cauHinhOcr.timPlaceholder')}
            aria-label={t('cauHinhOcr.timNhan')}
            value={tuKhoa}
            onChange={(e) => setTuKhoa(e.target.value)}
          />
          <Select<DocumentType>
            allowClear
            className="w-full sm:w-48"
            placeholder={t('cauHinhOcr.tatCaLoai')}
            aria-label={t('cauHinhOcr.locLoai')}
            value={loai}
            onChange={setLoai}
            options={DOCUMENT_TYPES.map((v) => ({ value: v, label: t(NHAN_LOAI[v]) }))}
          />
          <Select<LocTrangThai>
            className="w-full sm:w-44"
            aria-label={t('cauHinhOcr.locTrangThai')}
            value={trangThai}
            onChange={setTrangThai}
            options={[
              { value: 'tat-ca', label: t('cauHinhOcr.tatCaTrangThai') },
              { value: 'bat', label: t(BADGE_DANG_DUNG.bat.khoa) },
              { value: 'tat', label: t(BADGE_DANG_DUNG.tat.khoa) },
            ]}
          />
        </div>

        <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          {q.error && !q.data ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <span className="min-w-0 text-sm text-danger-700">
                {t('cauHinhOcr.loiDanhSach', { loi: dichLoi(q.error) })}
              </span>
              <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          ) : (
            <>
              <DataTable<OcrSchemaListItem>
                caption={t('cauHinhOcr.bangCaption')}
                rowKey="id"
                dataSource={dong}
                columns={columns}
                loading={dangTai}
                size="middle"
                scroll={{ x: 'max-content' }}
                pagination={false}
                locale={{
                  emptyText: dangTai ? (
                    ' '
                  ) : (
                    <Empty
                      icon={FileTextOutlined}
                      title={coLoc ? t('cauHinhOcr.rongLoc') : t('cauHinhOcr.rongThat')}
                      description={coLoc ? undefined : t('cauHinhOcr.rongThatMo')}
                      action={
                        coLoc || !coTao ? undefined : (
                          <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setMoTao(true)}>
                            {t('cauHinhOcr.taoLoaiDauTien')}
                          </Button>
                        )
                      }
                    />
                  ),
                }}
              />
              {dong.length > 0 && (
                <div className="border-t border-default px-4 py-3">
                  <span className="text-xs text-content-muted" role="status">
                    {coLoc
                      ? t('cauHinhOcr.dangHienLoc', { so: soVN(dong.length, locale), tong: soVN(tatCa.length, locale) })
                      : t('cauHinhOcr.dangHien', { so: soVN(dong.length, locale) })}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {moTao && (
        <HopLoaiChungTu
          open
          dangGui={ghi.tao.isPending}
          onClose={() => setMoTao(false)}
          onTao={async (du) => {
            const kq = await ghi.tao.mutateAsync(du);
            setMoTao(false);
            di(`/xu-ly/cau-hinh-ocr/${kq.id}`);
          }}
        />
      )}
      <HopXacNhanThaoTac
        open={canXoa !== null}
        tieuDe={t('cauHinhOcr.xoaLoaiHoi', { ten: canXoa?.name ?? '' })}
        than={t('cauHinhOcr.xoaLoaiThan')}
        nhanXacNhan={t('cauHinhOcr.xoaLoai')}
        nguyHiem
        dangChay={ghi.xoa.isPending}
        loi={ghi.xoa.error ? dichLoi(ghi.xoa.error) : null}
        onClose={() => setCanXoa(null)}
        onXacNhan={() =>
          canXoa && ghi.xoa.mutate({ id: canXoa.id, ten: canXoa.name }, { onSuccess: () => setCanXoa(null) })
        }
      />
    </div>
  );
}
